"use client";

import { Icons } from "@/components/icons";
import { MobileDrawer } from "@/components/mobile-drawer";
import { buttonVariants } from "@/components/ui/button";
import ShoppingCartComponent from "@/components/ui/shopping-cart";
import { easeInOutCubic } from "@/lib/animation";
import { siteConfig } from "@/lib/config";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion, useAnimation } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { useTheme } from "next-themes";

export function Header() {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // After mounting, we have access to the theme
  useEffect(() => setMounted(true), []);

  // This is backwards from what you might expect - we want light logo on dark background
  // and dark logo on light background (for better contrast)
  const logoSrc = mounted && theme === "dark" ? "/kled-io-light.png" : "/kled-io-dark.png";

  return (
    <header className="sticky top-0 h-auto z-50 p-0 bg-background/60 backdrop-blur">
      <div className="flex justify-between items-center container mx-auto p-2">
        <Link
          href="/"
          title="brand-logo"
          className="relative mr-6 flex items-center space-x-2"
        >
          <div className="w-32 h-8 relative">
            <Image 
              src={logoSrc}
              alt={siteConfig.name}
              fill
              style={{ objectFit: 'contain' }}
              priority
            />
          </div>
        </Link>
        
        <div className="hidden md:flex items-center space-x-6">
          {siteConfig.navbar.items.map((item, idx) => (
            <Link
              key={idx}
              href={item.url}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {item.text}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-4">
          {/* Shopping Cart */}
          <ShoppingCartComponent />
          
          {/* Theme toggle */}
          <button
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Toggle theme"
          >
            {mounted && theme === "dark" ? (
              <Icons.sun className="h-5 w-5" />
            ) : (
              <Icons.moon className="h-5 w-5" />
            )}
          </button>
          
          {/* Get Started button - Links to API Keys page */}
          <div className="hidden lg:block">
            <Link
              href="/account/api-keys"
              className={cn(
                buttonVariants({ variant: "default" }),
                "h-8 text-primary-foreground rounded-lg group tracking-tight font-medium",
                "bg-emerald-500 hover:bg-emerald-600"
              )}
            >
              {siteConfig.cta}
            </Link>
          </div>
          
          {/* Login button */}
          <div className="hidden lg:block">
            <Link
              href="/auth/login"
              className={cn(
                buttonVariants({ variant: "outline" }),
                "h-8 rounded-lg group tracking-tight font-medium",
                "border-emerald-500 text-emerald-500 hover:bg-emerald-500/10"
              )}
            >
              Login
            </Link>
          </div>
          
          <div className="mt-2 cursor-pointer block md:hidden">
            <MobileDrawer />
          </div>
        </div>
      </div>
      <hr className="absolute w-full bottom-0" />
    </header>
  );
}
